import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { onContactLinkClick } from '@/lib/contactLinks';

const CookiePolicy = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <section className="py-20 border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/" onClick={scrollToTop}>
            <Button variant="ghost" className="mb-8 text-gray-400 hover:text-white">
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back to Home
            </Button>
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Cookie Policy</h1>
          <p className="text-gray-400 text-lg">
            Last Updated: April 17, 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 space-y-12">
          {/* Introduction */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-white">1. Introduction</h2>
            <p className="text-gray-300 leading-relaxed">
              This Cookie Policy explains how RADIUS CONSULTING ("we", "us", or "our") uses cookies and similar technologies on our website{' '}
              <a href="https://radiusconsulting.co.za" className="text-red-500 hover:text-red-400">
                https://radiusconsulting.co.za
              </a>
              {' '}("the Site").
            </p>
            <p className="text-gray-300 leading-relaxed">
              By using our Site, you consent to the use of cookies in accordance with this Cookie Policy. If you do not agree with our use of cookies, you should change your browser settings accordingly or discontinue use of the Site.
            </p>
          </div>

          {/* What Are Cookies */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-white">2. What Are Cookies?</h2>
            <p className="text-gray-300 leading-relaxed">
              Cookies are small text files that are placed on your device (computer, smartphone, or tablet) when you visit a website. They are widely used to make websites work more efficiently and provide information to the site owners.
            </p>
            <p className="text-gray-300 leading-relaxed">
              Cookies can be "persistent" or "session" cookies:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong className="text-white">Session cookies</strong> - These are temporary and are deleted when you close your browser.</li>
              <li><strong className="text-white">Persistent cookies</strong> - These remain on your device for a set period or until you delete them.</li>
            </ul>
          </div>

          {/* Cookies We Use */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-white">3. Cookies We Use</h2>
            <p className="text-gray-300 leading-relaxed">
              We use the following types of cookies on our Site:
            </p>

            {/* Essential Cookies */}
            <div className="space-y-3 mt-6">
              <h3 className="text-xl font-semibold text-white">3.1 Essential Cookies</h3>
              <p className="text-gray-300 leading-relaxed">
                These cookies are necessary for the Site to function properly. They enable core functionality such as security, network management, and accessibility.
              </p>
              <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
                <p className="text-sm text-gray-400"><strong className="text-white">Purpose:</strong> Basic site functionality, security</p>
                <p className="text-sm text-gray-400"><strong className="text-white">Duration:</strong> Session</p>
                <p className="text-sm text-gray-400"><strong className="text-white">Can be disabled:</strong> No (required for site operation)</p>
              </div>
            </div>

            {/* Analytics Cookies */}
            <div className="space-y-3 mt-6">
              <h3 className="text-xl font-semibold text-white">3.2 Analytics Cookies (Google Analytics 4)</h3>
              <p className="text-gray-300 leading-relaxed">
                We use Google Analytics 4 to collect information about how visitors use our Site. This helps us understand user behavior, improve our Site, and optimize user experience.
              </p>
              <div className="bg-gray-900 rounded-lg p-4 border border-gray-800 space-y-2">
                <p className="text-sm text-gray-400"><strong className="text-white">Provider:</strong> Google LLC</p>
                <p className="text-sm text-gray-400"><strong className="text-white">Tracking ID:</strong> G-2TKBXR19LV</p>
                <p className="text-sm text-gray-400"><strong className="text-white">Purpose:</strong> Website analytics, user behavior tracking</p>
                <p className="text-sm text-gray-400"><strong className="text-white">Data collected:</strong> Page views, session duration, traffic sources, device information, geographic location (country/city level)</p>
                <p className="text-sm text-gray-400"><strong className="text-white">Duration:</strong> Up to 2 years</p>
                <p className="text-sm text-gray-400"><strong className="text-white">Can be disabled:</strong> Yes (via browser settings or opt-out)</p>
              </div>
              <p className="text-gray-300 text-sm mt-2">
                Learn more:{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-red-500 hover:text-red-400">
                  Google Privacy Policy
                </a>
              </p>
            </div>

            {/* Functionality Cookies */}
            <div className="space-y-3 mt-6">
              <h3 className="text-xl font-semibold text-white">3.4 Functionality Cookies</h3>
              <p className="text-gray-300 leading-relaxed">
                These cookies allow us to remember choices you make (such as your cookie consent preferences) and provide enhanced, more personalized features.
              </p>
              <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
                <p className="text-sm text-gray-400"><strong className="text-white">Purpose:</strong> Remember user preferences, cookie consent</p>
                <p className="text-sm text-gray-400"><strong className="text-white">Duration:</strong> Up to 1 year</p>
                <p className="text-sm text-gray-400"><strong className="text-white">Can be disabled:</strong> Yes</p>
              </div>
            </div>
          </div>

          {/* Managing Cookies */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-white">4. How to Manage Cookies</h2>
            <p className="text-gray-300 leading-relaxed">
              You have the right to decide whether to accept or reject cookies. You can manage your cookie preferences through:
            </p>

            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-white">4.1 Cookie Consent Banner</h3>
                <p className="text-gray-300 leading-relaxed">
                  When you first visit our Site, you'll see a cookie consent banner allowing you to accept or reject non-essential cookies.
                </p>
              </div>

              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-white">4.2 Browser Settings</h3>
                <p className="text-gray-300 leading-relaxed">
                  Most web browsers allow you to control cookies through their settings. You can typically:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                  <li>View and delete cookies</li>
                  <li>Block third-party cookies</li>
                  <li>Block cookies from specific sites</li>
                  <li>Block all cookies</li>
                  <li>Delete all cookies when you close your browser</li>
                </ul>
                <p className="text-gray-300 leading-relaxed mt-3">
                  For instructions on managing cookies in your browser:
                </p>
                <ul className="list-none text-gray-300 space-y-1 ml-4">
                  <li>
                    • <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-red-500 hover:text-red-400">Google Chrome</a>
                  </li>
                  <li>
                    • <a href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop" target="_blank" rel="noopener noreferrer" className="text-red-500 hover:text-red-400">Mozilla Firefox</a>
                  </li>
                  <li>
                    • <a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-red-500 hover:text-red-400">Apple Safari</a>
                  </li>
                  <li>
                    • <a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-red-500 hover:text-red-400">Microsoft Edge</a>
                  </li>
                </ul>
              </div>

              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-white">4.3 Google Analytics Opt-Out</h3>
                <p className="text-gray-300 leading-relaxed">
                  You can opt out of Google Analytics tracking by installing the{' '}
                  <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-red-500 hover:text-red-400">
                    Google Analytics Opt-out Browser Add-on
                  </a>.
                </p>
              </div>
            </div>

            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mt-6">
              <p className="text-red-300 text-sm">
                <strong>Note:</strong> Disabling cookies may affect the functionality of our Site and your user experience. Some features may not work properly if cookies are disabled.
              </p>
            </div>
          </div>

          {/* Third-Party Cookies */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-white">5. Third-Party Cookies</h2>
            <p className="text-gray-300 leading-relaxed">
              In addition to our own cookies, we use third-party cookies from trusted partners to help us analyze site usage and improve our services. These third parties include:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong className="text-white">Google Analytics</strong> - Web analytics</li>
              <li><strong className="text-white">Emergent</strong> - Development and hosting platform</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-3">
              These third parties have their own privacy policies governing the use of cookies. We encourage you to review their policies.
            </p>
          </div>

          {/* Updates to Policy */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-white">6. Updates to This Cookie Policy</h2>
            <p className="text-gray-300 leading-relaxed">
              We may update this Cookie Policy from time to time to reflect changes in our practices or for legal, regulatory, or operational reasons. The "Last Updated" date at the top of this page indicates when the policy was last revised.
            </p>
            <p className="text-gray-300 leading-relaxed">
              We encourage you to review this Cookie Policy periodically to stay informed about how we use cookies.
            </p>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-white">7. Contact Us</h2>
            <p className="text-gray-300 leading-relaxed">
              If you have any questions about this Cookie Policy or our use of cookies, please contact us:
            </p>
            <div className="bg-gray-900 rounded-lg p-6 border border-gray-800 space-y-2">
              <p className="text-gray-300"><strong className="text-white">RADIUS CONSULTING</strong></p>
              <p className="text-gray-300">Email:{' '}
                <a href="mailto:mishaan@radiusconsulting.co.za" onClick={onContactLinkClick} className="text-red-500 hover:text-red-400">
                  mishaan@radiusconsulting.co.za
                </a>
              </p>
              <p className="text-gray-300">Website:{' '}
                <a href="https://radiusconsulting.co.za" className="text-red-500 hover:text-red-400">
                  https://radiusconsulting.co.za
                </a>
              </p>
            </div>
          </div>

          {/* Related Policies */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-white">8. Related Policies</h2>
            <p className="text-gray-300 leading-relaxed">
              For more information about how we handle your personal data, please refer to:
            </p>
            <ul className="list-none text-gray-300 space-y-2">
              <li>
                •{' '}
                <Link to="/privacy-policy" onClick={scrollToTop} className="text-red-500 hover:text-red-400">
                  Privacy Policy
                </Link>
              </li>
              <li>
                •{' '}
                <Link to="/terms-of-service" onClick={scrollToTop} className="text-red-500 hover:text-red-400">
                  Terms of Service
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 border-t border-gray-800">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Questions About Our Cookie Policy?
          </h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            We're committed to transparency about how we use cookies. If you have any questions, we're here to help.
          </p>
          <Link to="/contact" onClick={scrollToTop}>
            <Button className="btn-primary">
              Contact Us
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CookiePolicy;
